import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { FaExchangeAlt } from 'react-icons/fa'

import cogoToast from '@successtar/cogo-toast'
import { useConfigStore } from '@renderer/stores/configState'
import { FrameOption, FrameConfig } from '@renderer/types/configState'
import PrintPreview, { PrintPreviewRef } from '@renderer/components/PrintPreview'
import { Button } from '@renderer/components'
import PhotoFrame from '@renderer/components/PhotoFrame'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { usePrintStore } from '@renderer/stores/printStore'
import {
  processAndUploadStrip,
  stateUploader
} from '@renderer/utils/pipeline/outputOrchestrator'
import { initiatePrint } from '@renderer/utils/pipeline/printService'
import type { ProcessingContext } from '@renderer/types/outputPipeline.types'
import { waitPrintResultMinDisplay } from '@renderer/utils/printResultMinDisplay'
import { navigateAfterOutputsReady } from '@renderer/utils/navigateAfterOutputs'

interface UserContext {
  user?: {
    email: string
  }
}

const EditSelectPage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const context = useOutletContext<UserContext | null>()
  const { gallery, setSessionState, userPreferences, id, logActivity } =
    useSessionStateStore()
  const { getFrame, config: globalConfig, frames } = useConfigStore()
  const config = globalConfig?.pages.select
  const { primaryColor, secondaryColor } = globalConfig?.theme || {
    primaryColor: '#3B82F6',
    secondaryColor: '#FFFFFF'
  }
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([])
  const [remainingTime, setRemainingTime] = useState<string>('--')
  const [isUploading, setIsUploading] = useState(false)
  const previewRef = useRef<PrintPreviewRef>(null)
  const [mirroredPhotos, setMirroredPhotos] = useState<Set<string>>(new Set())
  const [isAllMirrored, setIsAllMirrored] = useState(false)
  const [isSelectionLocked, setIsSelectionLocked] = useState(false)
  const hasProcessedRef = useRef(false)
  const { downloadLocal } = useSettingsStore()
  const { isPrintingEnabled, getPrinterForFrame } = usePrintStore()
  const printJobSentRef = useRef<boolean>(false)
  const isMountedRef = useRef(true)

  // Initialize countdown hook
  const selectionCountdown = useCountdown(config?.settings?.selectionTimeout || 0)

  const getFrameConfig = (frame: FrameOption | null): FrameConfig => {
    if (!frame)
      return {
        id: '',
        baseImage: '',
        drawParams: {
          canvasWidth: 1200,
          canvasHeight: 1800,
          backgroundColor: '#FFFFFF',
          images: []
        }
      }
    return {
      id: frame._id,
      baseImage: frame.thumbnail,
      drawParams: frame.drawParams
    }
  }

  const { settings: currentPrinterSettings } = getPrinterForFrame(userPreferences.frameId || '')
  const printResolutionScale = currentPrinterSettings.printResolutionScale || 1

  // Get scaled frame for printing/preview
  const maybeScaledFrame = getFrame(userPreferences.frameId || frames[0]?._id, printResolutionScale)
  const selectedScaledFrame = maybeScaledFrame || null
  const frameConfig = getFrameConfig(selectedScaledFrame)

  // Get unique photo types from frame configuration
  const uniquePhotoTypes = frameConfig.drawParams.images
    .filter((img) => img.type.startsWith('photo-'))
    .map((img) => img.type)
    .filter((value, index, self) => self.indexOf(value) === index)

  const maxSelectablePhotos = uniquePhotoTypes?.length || 0

  const isPotrait =
    (globalConfig?.screenSize.height || 1080) > (globalConfig?.screenSize.width || 1920)

  // Handle countdown complete for editing mode
  const handleCountdownComplete = (): void => {
    logActivity('Selection timed out for editing, proceeding with selected photos.')
    setIsSelectionLocked(true)
    setSelectedPhotos((prevSelected: string[]) => {
      let finalSelection = prevSelected

      if (prevSelected.length < maxSelectablePhotos) {
        const remainingNeeded = maxSelectablePhotos - prevSelected.length
        const unselectedPhotos = gallery.capturedItems
          .filter((item) => !prevSelected.includes(item.photoUrl))
          .slice(0, remainingNeeded)
        const autoSelected = unselectedPhotos.map((item) => item.photoUrl)
        finalSelection = [...prevSelected, ...autoSelected]
      }

      // Use setTimeout to ensure state has been updated before calling handleContinue
      setTimeout(() => {
        handleContinueWithPhotos(finalSelection)
      }, 1500)

      return finalSelection
    })
  }

  const handleContinueWithPhotos = async (photosToProcess: string[]): Promise<void> => {
    selectionCountdown.stop()
    setIsUploading(true)
    setIsSelectionLocked(true)
    const processingStartedAt = Date.now()
    let didAttemptPrint = false

    setSessionState({
      // Stats tracked by Gallery API
    })

    try {
      // Generate and upload photo strip
      const stripBlob = await previewRef.current?.exportImage()
      if (!stripBlob) throw new Error('Failed to create strip preview')

      const sessionId = gallery.originalSessionId || id || ''
      const galleryId = gallery.originalSessionId || gallery.id
      const ctx: ProcessingContext = {
        context,
        projectId,
        sessionId,
        galleryId,
        downloadLocal,
        isOnline: true
      }

      if (isPrintingEnabled && !printJobSentRef.current) {
        didAttemptPrint = true
        printJobSentRef.current = true
        const { printerName, settings } = getPrinterForFrame(userPreferences.frameId || '')
        try {
          await initiatePrint(stripBlob, printerName, 1, settings, true)
          cogoToast.success('Printing Initialized')
        } catch (err) {
          cogoToast.error(
            `Failed to print: ${err instanceof Error ? err.message : 'Unknown error'}`
          )
        }
      }

      const stripResult = await processAndUploadStrip(stripBlob, ctx)

      const localUrl = stripResult?.localUrl ?? URL.createObjectURL(stripBlob)
      const updatedGallery = {
        ...gallery,
        capturedItems: [
          ...gallery.capturedItems,
          {
            id: `dreambooth-edit-strip-${id}`,
            stripUrl: localUrl,
            photoUrl: '',
            timestamp: new Date()
          }
        ]
      }

      setSessionState({
        gallery: updatedGallery
      })

      stateUploader.uploadSessionState()
      const minDisplaySec = useSettingsStore.getState().printResultMinDisplaySeconds
      if (didAttemptPrint && minDisplaySec > 0) {
        await waitPrintResultMinDisplay(processingStartedAt, minDisplaySec, {
          isStillMounted: () => isMountedRef.current
        })
      }
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    } catch (error) {
      console.error('Error in continue process for editing:', error)
      cogoToast.error('There was an error processing your photos. Please report this issue.')

      // Log processing failure for editing
      logActivity(
        `Processing failed for ${photosToProcess.length} photos in edit mode. Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { mode: 'edit', photoCount: photosToProcess.length }
      )
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    } finally {
      setIsUploading(false)
      setIsSelectionLocked(false)
    }
  }

  // Skip selection if disabled
  useEffect(() => {
    const autoSelectAndProcess = async (): Promise<void> => {
      if (!config?.settings.enabled && !hasProcessedRef.current) {
        hasProcessedRef.current = true

        try {
          // Calculate how many photos we need based on frame configuration
          const photoSlots = frameConfig.drawParams.images.filter((img) =>
            img.type.startsWith('photo-')
          ).length

          // Auto select first N photos
          const autoSelected = gallery.capturedItems.slice(0, photoSlots)
          const selectedUrls = autoSelected.map((item) => item.photoUrl)
          setSelectedPhotos(selectedUrls)

          // Give more time for PrintPreview to be ready
          await new Promise((resolve) => setTimeout(resolve, 2000))

          // Call handleContinueWithPhotos with the selected photos
          await handleContinueWithPhotos(selectedUrls)
        } catch (error) {
          console.error('Error in auto processing for editing:', error)
          cogoToast.error('There was an error processing your photos. Please try again.')
          hasProcessedRef.current = false
        }
      }
    }

    autoSelectAndProcess()
  }, [config?.settings.enabled, frameConfig, gallery.capturedItems])

  // Initialize countdown and handle initial setup
  useEffect((): void | (() => void) => {
    isMountedRef.current = true
    stateUploader.uploadSessionState()

    const selectionTimeout = config?.settings?.selectionTimeout
    if (selectionTimeout && selectionTimeout > 0) {
      // Set up countdown callbacks
      selectionCountdown.setOnTick(() => {
        setRemainingTime(selectionCountdown.getFormattedRemainingSeconds())
      })

      selectionCountdown.setOnComplete(() => {
        handleCountdownComplete()
      })

      selectionCountdown.start()
    }

    return (): void => {
      isMountedRef.current = false
      selectionCountdown.stop()
    }
  }, [])

  const handlePhotoClick = (photoUrl: string): void => {
    if (isSelectionLocked) return

    setSelectedPhotos((prev) => {
      let newSelection
      if (prev.includes(photoUrl)) {
        newSelection = prev.filter((url) => url !== photoUrl)
      } else if (prev.length < maxSelectablePhotos) {
        newSelection = [...prev, photoUrl]
      } else {
        newSelection = prev
      }
      return newSelection
    })
  }

  // Modified handleContinue to use the new function
  const handleContinue = async (): Promise<void> => {
    await handleContinueWithPhotos(selectedPhotos)
  }

  // Separate countdown component to prevent preview re-renders
  const CountdownDisplay = React.memo(() => (
    <span className="text-center text-carbon text-3xl">{remainingTime}</span>
  ))
  CountdownDisplay.displayName = 'CountdownDisplay'

  // Update the loading state
  const isLoading: boolean = isUploading

  // Update the button text to show processing state
  const buttonText: string = isLoading
    ? 'Processing...'
    : `Next (${selectedPhotos.length}/${maxSelectablePhotos})`

  // If selection is disabled, show loading state with PrintPreview underneath
  if (!config?.settings.enabled) {
    return (
      <div className="w-full h-full flex bg-whiteout">
        {/* Hidden PrintPreview for auto-processing */}
        <div className="hidden">
          <PrintPreview
            ref={previewRef}
            frameConfig={frameConfig}
            selectedPhotos={selectedPhotos.map((photo) =>
              mirroredPhotos.has(photo) ? `mirror:${photo}` : photo
            )}
            isInteractive={!isSelectionLocked}
            sessionId={id || ''}
          />
        </div>

        {/* Loading Overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-whiteout z-50">
          <div className="flex flex-col items-center gap-8 p-8 bg-white rounded-2xl shadow-2xl">
            <h1 className="text-4xl text-carbon font-bold">Processing Photos</h1>
            <div
              className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin"
              style={{
                borderColor: primaryColor,
                borderTopColor: 'transparent'
              }}
            />
            <p className="text-lg text-carbon/60">Please wait while we process your photos...</p>
          </div>
        </div>
      </div>
    )
  }

  // Check if gallery data is available
  if (!gallery || !gallery.capturedItems || gallery.capturedItems.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-whiteout">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">No Photos Available</h1>
          <p className="text-gray-600 mb-6">
            Please go back to the welcome page and load a gallery first.
          </p>
          <Button
            text="Back to Welcome"
            onClick={() => navigate(`/${projectId}`)}
            variant="primary"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex bg-whiteout">
      {/* Left Content - Photo Selection */}
      <div className="flex-1 flex flex-col w-full max-w-[70%]">
        <div className="px-12 mt-16 mb-8 flex items-center justify-center">
          <button
            type="button"
            onClick={() => {
              if (isSelectionLocked) return
              // Mirror all photos
              const newMirroredPhotos = new Set(mirroredPhotos)
              const willMirrorAll = gallery.capturedItems.some(
                (item) => !mirroredPhotos.has(item.photoUrl)
              )

              gallery.capturedItems.forEach((item) => {
                if (willMirrorAll) {
                  newMirroredPhotos.add(item.photoUrl)
                } else {
                  newMirroredPhotos.delete(item.photoUrl)
                }
              })

              setIsAllMirrored(willMirrorAll)
              setMirroredPhotos(newMirroredPhotos)
            }}
            disabled={isSelectionLocked}
            title={isSelectionLocked ? 'Selection locked' : 'Mirror Photos'}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors shadow-md ${
              isAllMirrored
                ? 'text-white hover:bg-opacity-90'
                : 'bg-white hover:bg-gray-100 text-carbon'
            } ${isSelectionLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{
              backgroundColor: isAllMirrored ? primaryColor : undefined
            }}
          >
            <FaExchangeAlt className={`text-lg ${isAllMirrored ? 'text-white' : 'text-carbon'}`} />
            <span className={isAllMirrored ? 'text-sm text-white' : 'text-sm text-carbon'}>
              Mirror
            </span>
          </button>
        </div>
        <div
          className={`flex-1 overflow-y-auto px-16 pb-12 relative ${isSelectionLocked ? 'pointer-events-none' : ''}`}
        >
          {isSelectionLocked && (
            <div className="absolute inset-0 bg-carbon/5 backdrop-blur-[1px] z-10 flex items-center justify-center">
              <div className="bg-white/90 px-6 py-3 rounded-lg shadow-lg">
                <span className="text-xl text-carbon">
                  {isUploading ? 'Processing photos...' : 'Selection locked'}
                </span>
              </div>
            </div>
          )}
          <div className={`grid ${isPotrait ? 'grid-cols-1' : 'grid-cols-3'} gap-6`} role="listbox">
            {gallery.capturedItems.map((item, index) => {
              if (!item.photoUrl) return null
              const isSelected = selectedPhotos.includes(item.photoUrl)
              const isMirrored = mirroredPhotos.has(item.photoUrl)
              return (
                <div key={index} className="relative group">
                  <button
                    type="button"
                    onClick={() => handlePhotoClick(item.photoUrl)}
                    disabled={isSelectionLocked}
                    role="option"
                    className={`
                      group relative overflow-hidden rounded-xl transition-all duration-300 w-full
                      ${isSelected ? 'ring-4 scale-95' : 'hover:scale-[0.98]'}
                      ${isSelectionLocked ? 'opacity-75 cursor-not-allowed' : ''}
                    `}
                    style={{
                      boxShadow: isSelected ? `0 0 0 4px ${primaryColor}` : ''
                    }}
                  >
                    <PhotoFrame
                      src={item.photoUrl || ''}
                      alt={`Gallery Photo ${index + 1}`}
                      displayRatio={userPreferences.framePhotoRatio || 1}
                      isMirrored={isMirrored}
                      className={isSelected ? 'grayscale' : ''}
                    />
                    <div
                      className={`
                        absolute inset-0 flex items-center justify-center
                        bg-black/50 transition-opacity duration-300
                        ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                      `}
                    >
                      <div className="text-center text-white">
                        <div className="text-4xl mb-2">{isSelected ? '✓' : '+'}</div>
                        <p className="text-lg font-medium">
                          {isSelected ? 'Selected' : 'Click to Select'}
                        </p>
                      </div>
                    </div>
                    {isSelected && (
                      <div
                        className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg"
                        style={{
                          backgroundColor: primaryColor
                        }}
                      >
                        <span
                          style={{
                            color: secondaryColor
                          }}
                          className="text-lg font-bold"
                        >
                          {selectedPhotos.indexOf(item.photoUrl || '') + 1}
                        </span>
                      </div>
                    )}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Right Panel - Print Preview */}
      <div
        className={`bg-whiteout shadow-xl flex flex-col items-center justify-center gap-4 w-full ${isPotrait ? 'p-12 max-w-[55%]' : 'p-8 max-w-[35%]'}`}
      >
        <div className="w-full px-4 mt-16">
          <h1 className="text-4xl text-center text-carbon font-bold">Create New Strip</h1>
        </div>
        <div className="w-[426px]">
          <div className="mb-4 text-center text-carbon/60">
            <p>Swipe to move, pinch to zoom</p>
          </div>
          <PrintPreview
            ref={previewRef}
            frameConfig={frameConfig}
            selectedPhotos={selectedPhotos.map((photo) =>
              mirroredPhotos.has(photo) ? `mirror:${photo}` : photo
            )}
            className="w-full shadow-xl"
            sessionId={id || ''}
          />
        </div>

        <div className="flex flex-col items-center gap-4">
          <CountdownDisplay />
          <Button
            text={buttonText}
            onClick={handleContinue}
            data-nav-to="result"
            disabled={selectedPhotos.length !== maxSelectablePhotos || isUploading}
            variant={
              selectedPhotos.length === maxSelectablePhotos && !isUploading
                ? 'primary'
                : 'secondary'
            }
          />
        </div>
      </div>
    </div>
  )
}

export default EditSelectPage
